import React, { useEffect, useRef, useState } from 'react'

import 'chart.js/auto'
import type { Chart, ChartEvent, ChartType, ScriptableContext } from 'chart.js'
import { Bar } from 'react-chartjs-2'

import { TDatasetMarker } from 'components_new/molecules/charts/chartjs-plugins'
import { OptionProps } from 'components_new/molecules/OptionsMenu'
import {
  TChartClick,
  TChartData,
  TChartDataset,
  TChartVariant
} from '../types'

import LoadingSection from 'components_new/molecules/LoadingSection'

import ChartOptionsMenu from '../ChartOptionsMenu'
import ChartWrapper from '../ChartWrapper'

import { drawBandOnHover } from '../plugins/drawBandOnHover'
import { drawCellGuides } from '../plugins/drawCellGuides'
// REFERENCE-LINES
import { drawHorizontalLines } from '../plugins/drawHorizontalLines'
import { drawLatestValueLabel } from '../plugins/drawLatestValueLabel'
import { drawMarkers } from '../plugins/drawMarkers'
import { indicateClickOnBarHover } from '../plugins/indicateClickOnBarHover'
import { EMPTY_CHART_CLICK } from '../utils/constants'
import { resolveMarkers } from '../utils/markers'
import {
  getChartBackgroundColorOptions,
  getChartBorderColorOptions,
  getChartBorderDashOptions,
  getChartBorderWidthOptions,
  getChartDatasetsOptions,
  getChartInteractionOptions,
  getChartLegendOptions,
  getChartPointBackgroundColorOptions,
  getChartPointColorOptions,
  getChartScalesOptions,
  getChartTooltipOptions
} from '../utils/options'
import { handleOnSegmentClick } from './utils'
import { isArray } from 'lodash'
import { useTranslation } from 'language/translator'
import { formatValue } from 'components_new/organisms/Widget/Types/utils'
import { useTheme } from '@mui/material'

// A chart in a table cell: thin bars, square at the bottom, no padding.
const COMPACT_BAR_WIDTH = 0.6
const COMPACT_RADIUS = 1.5

interface BarChartProps {
  /** Start the y-axis at 0, also when the axis is hidden. */
  beginYAtZero?: boolean

  /**
   * Sized for a table cell: thin bars rounded only on top, a baseline, no
   * padding and no animation.
   */
  compact?: boolean
  data: TChartData
  decimals?: number
  flexItem?: boolean
  focusedMarkerId?: string

  /**
   * compact only: a short grey stub where the first dataset has no value,
   * so missing reads apart from 0 and still has a tooltip.
   */
  missingStub?: boolean
  loading?: boolean
  onClickComment?: (index: number) => void
  onMarkerClick?: (markers: TDatasetMarker[]) => void
  onSegmentClick?: (segmentIndex: number | null) => void
  options?: OptionProps[] | ((payload: TChartClick) => OptionProps[])
  orientation?: 'horizontal' | 'vertical'
  significantFigures?: number

  /** Overrides the tooltip's surface, e.g. opaque and above a table. */
  tooltipStyle?: Partial<CSSStyleDeclaration>
  variant: TChartVariant

  /** A fixed y-axis. With clamp, values outside it are drawn at its edge. */
  yRange?: { clamp: boolean; max: number; min: number }
}

/** The scales with a fixed y-axis, when there is one. */
const withYRange = (
  scales: ReturnType<typeof getChartScalesOptions>,
  yRange: BarChartProps['yRange']
) =>
  yRange
    ? { ...scales, y: { ...scales?.y, max: yRange.max, min: yRange.min } }
    : scales

const BarChart = (props: BarChartProps) => {
  const {
    beginYAtZero,
    compact,
    data,
    decimals,
    flexItem,
    focusedMarkerId,
    missingStub,
    loading,
    onMarkerClick,
    onSegmentClick,
    options: menuOptions,
    orientation,
    significantFigures,
    tooltipStyle,
    variant,
    yRange
  } = props

  const chartRef = useRef<any>(null)
  const theme = useTheme()
  const { t } = useTranslation()

  // Drawn with a minimum height in the same category as the missing value.
  const stub: TChartDataset | null = missingStub
    ? {
        color: theme.palette.grey[300],
        label: '',
        type: 'bar',
        unit: null,
        values: data.datasets[0].values.map((value) =>
          value === null ? 0 : null
        )
      }
    : null
  const datasets = stub ? [...data.datasets, stub] : data.datasets

  // compact: the first dataset's own value under the cursor, also when it
  // is clamped or missing.
  const getCompactRows: Parameters<
    typeof getChartTooltipOptions
  >[0]['getRows'] = (dataPoints) => {
    const [main] = data.datasets
    const value = main.values[dataPoints[0]?.dataIndex ?? 0]

    return [
      {
        color: isArray(main.color) ? '' : main.color ?? '',
        label: main.label,
        type: 'bar',
        unit: value === null ? '' : main.unit,
        value:
          value === null
            ? t('global', 'missing_value')
            : String(
              formatValue(
                value,
                null,
                decimals ?? 1,
                undefined,
                undefined,
                significantFigures
              )
            )
      }
    ]
  }

  const [clicked, setClicked] = useState<TChartClick>(EMPTY_CHART_CLICK)

  useEffect(() => {
    chartRef.current?.draw()
  }, [focusedMarkerId])
  const open =
    Boolean(clicked?.position?.left !== 0) ||
    Boolean(clicked?.position?.top !== 0)

  const handleClose = () => {
    setClicked((prevState) => ({
      ...prevState,
      position: {
        left: 0,
        top: 0
      }
    }))
  }

  const handleOnClickMarker = (args: {
    chart: Chart
    event: ChartEvent
    markers: TDatasetMarker[]
  }) => {
    const { markers } = args

    if (onMarkerClick) {
      onMarkerClick(markers)
    }
  }

  return (
    <ChartWrapper flexItem={Boolean(flexItem)}>
      {loading ? (
        <LoadingSection />
      ) : (
        <>
          <Bar
            data={{
              labels: data.labels,
              datasets: datasets.map((dataset) => {
                return {
                  backgroundColor: (ctx: ScriptableContext<ChartType>) =>
                    getChartBackgroundColorOptions({ ctx, dataset }),
                  borderColor: (ctx: any) =>
                    getChartBorderColorOptions({ ctx, dataset }),
                  borderDash: () => getChartBorderDashOptions({ dataset }),
                  ...(compact
                    ? {
                        barPercentage: COMPACT_BAR_WIDTH,
                        borderRadius: {
                          bottomLeft: 0,
                          bottomRight: 0,
                          topLeft: COMPACT_RADIUS,
                          topRight: COMPACT_RADIUS
                        },
                        borderSkipped: 'bottom' as const,
                        categoryPercentage: 1
                      }
                    : { borderRadius: 8, borderSkipped: false }),
                  borderWidth: (ctx: ScriptableContext<ChartType>) =>
                    getChartBorderWidthOptions({ ctx, dataset }),
                  ...(dataset === stub
                    ? { grouped: false, minBarLength: 2 }
                    : {}),
                  data: (dataset.values as (number | null)[]).map((value) =>
                    yRange?.clamp && value !== null
                      ? Math.min(Math.max(value, yRange.min), yRange.max)
                      : value
                  ),
                  label: dataset.label,
                  main: dataset.main,
                  markers: resolveMarkers(dataset.markers, data.periods),
                  order: dataset.order,
                  pointBackgroundColor: (ctx: ScriptableContext<ChartType>) =>
                    getChartPointBackgroundColorOptions({
                      ctx,
                      dataset,
                      variant
                    }),
                  pointBorderColor: getChartPointColorOptions,
                  type: dataset.type as 'bar' // actually ChartType
                }
              })
            }}
            options={{
              ...(compact ? { animation: false, layout: { padding: 0 } } : {}),
              datasets: getChartDatasetsOptions(),
              indexAxis: orientation === 'horizontal' ? 'y' : 'x',
              interaction: getChartInteractionOptions(),
              maintainAspectRatio: false,
              onClick: (event, _active, chart) =>
                handleOnSegmentClick({
                  _active,
                  callback: (params: TChartClick) => {
                    if (onSegmentClick) {
                      onSegmentClick(params.index)
                    }
                    setClicked(params)
                  },
                  chart,
                  event
                }),
              plugins: {
                drawCellGuides: {
                  color: theme.palette.grey[300],
                  disabled: !compact
                },
                // REFERENCE-LINES
                drawHorizontalLines: {
                  decimals,
                  lines: (data.horizontalLines ?? []).map((line) => ({
                    color: line.color ?? theme.palette.action.active,
                    label: line.label,
                    value: line.value
                  })),
                  significantFigures
                },
                drawLatestValueLabel: {
                  datasets: data.isTimeseries
                    ? data.datasets.map((ds) => {
                      const color = isArray(ds.color)
                        ? ds.color[ds.color.length - 1]
                        : ds.color

                      return {
                        color: color ?? theme.palette.action.active,
                        main: ds.main
                      }
                    })
                    : [],
                  decimals,
                  significantFigures
                },
                drawMarkers: {
                  focusedMarkerId,
                  onClick: handleOnClickMarker
                },
                legend: getChartLegendOptions({
                  nDatasets: data.datasets.length,
                  variant
                }),
                tooltip: getChartTooltipOptions({
                  compact,
                  decimals,
                  getRows: compact ? getCompactRows : undefined,
                  significantFigures,
                  unit: data.datasets[0].unit,
                  variant,
                  style: tooltipStyle
                })
              },
              responsive: true,
              scales: withYRange(
                getChartScalesOptions({
                  beginYAtZero,
                  isTimeseries: data.isTimeseries,
                  type: 'bar',
                  variant
                }),
                yRange
              )
            }}
            plugins={[
              drawBandOnHover,
              ...(compact ? [drawCellGuides] : []),
              ...(variant === 'detailed' ? [drawLatestValueLabel] : []),
              drawHorizontalLines, // REFERENCE-LINES
              ...(variant === 'detailed' ? [drawMarkers] : []),
              ...(variant === 'detailed' ? [indicateClickOnBarHover] : [])
            ]}
            // redraw={true} // uncomment in dev
            ref={chartRef}
          />

          {menuOptions ? (
            <ChartOptionsMenu
              clicked={clicked}
              onClose={handleClose}
              open={open}
              options={menuOptions}
            />
          ) : null}
        </>
      )}
    </ChartWrapper>
  )
}

export default BarChart
